import {Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import * as React from "react";
import {useState} from "react";
import {useList} from "crqlar-react-common/dist/components/CList/useList";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {getAmountFormatted} from "crqlar-react-common/dist/utils/currency.util";
import {GeneralSet} from "crqlar-react-common/dist/utils/generalSet";
import {useProfiles} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {findProfileById} from "crqlar-react-common/dist/utils/profile.util";
import {useReservations} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {useServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReservationFolioDetailsDialog from "./ReservationFolioDetailsDialog";
import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {useCurrentApp} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useNavigate} from "react-router-dom";
import {ListColumn} from "crqlar-react-common/dist/components/CList/CList";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    profileId?: string
    reservationId?: string
    reservationGroupId?: string
    open: boolean
    onClose: () => void
}

const ReservationFoliosDialog = (props: Props) => {

    const {t} = useTranslation()
    const navigate = useNavigate()
    // const [currentFolio, setCurrentFolio] = useState<Folio>()
    const appData = useCurrentApp()
    const sectorId = appData?.configuration?.sectorId
    const sector = useSector(sectorId)
    const readOnly = useReadOnly()

    const columns: ListColumn[] = [
        {
            name: "Name",
            id: "name",
            width: 'l'
        },
        {
            name: "Profile",
            id: "profile",
            width: 'grow'
        },
        {
            name: "Reservation",
            id: "reservation",
            width: 'xxxl'
        },
        {
            name: "Total",
            id: "total",
            width: 'l'
        },
        {
            name: "Status",
            id: "status",
            width: 'l'
        }
    ]

    const list = useList({
        list: (page, size, search, sortProp, sortDir) => {
            return FolioService.getAll({
                reservationIds: props.reservationId ? [props.reservationId] : undefined,
                reservationGroupIds: props.reservationGroupId ? [props.reservationGroupId] : undefined,
                limit: {
                    offset: page * size,
                    limit: size,
                    sort: [{field: sortProp, direction: sortDir}]
                }
            })
        },
        listId: "FoliosList",
        searchable: true,
        columns: columns,
        allColumns: columns,
        renderCell: (item, column) => {
            switch (column.id) {
                case "name":
                    return item.name
                case "profile":
                    let name = t('Unknown')
                    const profile = findProfileById(profiles, item.profileId)
                    if (profile) {
                        if (profile.nameOrdered) {
                            name = profile.nameOrdered
                        } else {
                            name = [profile.firstName, profile.lastName].filter(Boolean).join(' ')
                        }
                    } else {
                        const reservation = reservations.data?.find(reservation => reservation.id === item.reservationId)
                        if (reservation) {
                            name = [reservation.reservationRequestData?.firstName, reservation.reservationRequestData?.lastName].filter(Boolean).join(' ')
                            if (name) {
                                name = `${name} (req.)`
                            }
                        }
                    }

                    if (!name) {
                        name = t('Unknown')
                    }

                    return name
                case "reservation":
                    const reservation = reservations.data?.find(reservation => reservation.id === item.reservationId)
                    if (!reservation) return '-'

                    const service = services.find(service => service.id === reservation?.serviceId)
                    return (
                        <Stack
                            direction={'row'}
                            gap={1}
                            alignItems={'center'}
                            // onClick={e => {
                            //     e.stopPropagation()
                            //     navigate(`/reservations/${item.reservationId}`)
                            // }}
                        >
                            <Typography>
                                {service?.name} | {[
                                formatFullDMY(reservation.actualStartDate || reservation.expectedStartDate),
                                formatFullDMY(reservation.actualEndDate || reservation.expectedEndDate)
                            ].join(' - ')}

                            </Typography>

                            <IconButton
                                color={'primary'}
                            >
                                <OpenInNewIcon sx={{fontSize: '16px'}}/>
                            </IconButton>

                        </Stack>
                    )
                case "total":
                    return getAmountFormatted(item.total, '-')
                case "status":
                    return item.status
                default:
                    return null
            }
        },
        onItemClick: (item) => {
            // setCurrentFolio(item)
            // navigate(`/system/folios/${item.id}`)
            window.open(`/system/folios/${item.id}`, '_blank');
        },
        initialSortProp: "id",
        initialSortDir: "DESC",
        actionBar: () => (
            <Stack>
                <Button
                    id={'btn-create-folio'}
                    variant={'contained'}
                    disabled={readOnly}
                    onClick={async () => {
                        const folio = await FolioService.create({
                            name: sector?.name + ' ' + t('Res.'),
                            profileId: props.profileId,
                            reservationId: props.reservationId,
                            reservationGroupId: props.reservationGroupId
                        })
                        // setCurrentFolio(folio)
                        // navigate(`/system/folios/${folio.id}`)
                        window.open(`/system/folios/${folio.id}`, '_blank');
                    }}
                    startIcon={<AddIcon/>}
                >
                    {t('Create folio')}
                </Button>
            </Stack>
        )
    }, props.profileId, props.reservationGroupId, props.reservationId)

    const profileIds = new GeneralSet()
    const reservationIds = new GeneralSet()
    const serviceIds = new GeneralSet()
    list.items?.content?.forEach(item => {
        item.profileId && profileIds.add(item.profileId)
        item.reservationId && reservationIds.add(item.reservationId)
    })
    const profiles = useProfiles(profileIds.values() as string[])
    const reservations = useReservations(reservationIds.values() as string[])

    reservations.data?.forEach(reservation => {
        reservation.serviceId && serviceIds.add(reservation.serviceId)
    })
    const services = useServices(serviceIds.values() as string[])

    return (
        <Dialog
            open={props.open}
            onClose={() => {
                props.onClose()
            }}
            maxWidth={false}
        >
            <DialogTitle>
                {t('Folios')}
            </DialogTitle>
            <DialogContent>
                {list.renderList()}
            </DialogContent>

            {/*<ReservationFolioDetailsDialog*/}
            {/*    folioId={currentFolio?.id}*/}
            {/*    open={!!currentFolio}*/}
            {/*    onClose={() => {*/}
            {/*        setCurrentFolio(undefined)*/}
            {/*        list.refresh()*/}
            {/*    }}*/}
            {/*/>*/}

        </Dialog>
    );
}

export default ReservationFoliosDialog;
